import { useRoute } from '@react-navigation/native';
import { BatchLevelSubScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectNcFiles } from '@reducers/details';
import { selectFeatureFlags } from '@reducers/user';
import { FeatureFlagsAvailable } from '@components/featureFlags';
import { ITEM_RELATIONS_FEATURE_FLAG } from '@v2/pages/common/constants/base.constants';
import { role } from '@components/frame';
import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { NCFileLevelSubScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { JobLevelSubScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { PartLevelSubScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { ProductionOrderLevelSubScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { timelineAvailable } from '@config/navigationRestrictions';
import { ToolLevelSubScreen } from '@v2/pages/tools/details/common/enums/toolsDetails.enum';

export const useLevelsSubMenu = () => {
  const { safeIsMachinesScreenCheck } = useNavigationCheck();
  const route = useRoute();
  const ncFilesData = useSelector(selectNcFiles);
  const ncFiles = ncFilesData?.data ?? [];
  const featureFlags = useSelector(selectFeatureFlags);
  const isItemRelationsEnabled = FeatureFlagsAvailable(ITEM_RELATIONS_FEATURE_FLAG, featureFlags);

  const perspective = route.params?.perspective;

  const filterTimelineScreen = <T>(screens: T[], timelineScreen: T): T[] => {
    return timelineAvailable ? screens : screens.filter((screen) => screen !== timelineScreen);
  };

  // For machines screen: use MACHINE_NOW_SUB_TABS (hide Performance, Events)
  // For standalone details: use BATCH_TABS (hide Events only)
  const batchRestrictionCategory = safeIsMachinesScreenCheck ? RESTRICTION_CATEGORIES.MACHINE_NOW_SUB_TABS : RESTRICTION_CATEGORIES.BATCH_TABS;

  const batchLevelSubScreens = useMemo(() => {
    const screens = Object.values(BatchLevelSubScreen).filter((screen) => !isRestrictedForOperator(role || '', batchRestrictionCategory, screen));

    return filterTimelineScreen(screens, BatchLevelSubScreen.TIMELINE);
  }, [batchRestrictionCategory]);

  // For standalone NC File details: use NC_FILE_TABS (hide Times, Events, Performance)
  const ncFileLevelSubScreens = useMemo(() => {
    if (perspective && [Perspective.PART, Perspective.JOBS].includes(perspective) && ncFiles?.length !== 1) {
      return [];
    }

    const screens = Object.values(NCFileLevelSubScreen).filter(
      (screen) =>
        !isRestrictedForOperator(role || '', RESTRICTION_CATEGORIES.NC_FILE_TABS, screen) &&
        // Demo traceability tab is gated to dev/qa via feature flag.
        (isItemRelationsEnabled || screen !== NCFileLevelSubScreen.TRACEABILITY)
    );

    // Based on task SF-4963
    // return filterTimelineScreen(screens, NCFileLevelSubScreen.TIMELINE);
    return screens;
  }, [perspective, ncFiles?.length, isItemRelationsEnabled]);

  const jobsLevelSubScreens = useMemo(() => {
    const screens = [JobLevelSubScreen.GENERAL, JobLevelSubScreen.TIMELINE, JobLevelSubScreen.DOCUMENTS];
    return filterTimelineScreen(screens, JobLevelSubScreen.TIMELINE);
  }, []);

  const productionOrderLevelSubScreens = useMemo(() => {
    const screens = [ProductionOrderLevelSubScreen.GENERAL, ProductionOrderLevelSubScreen.TIMELINE, ProductionOrderLevelSubScreen.DOCUMENTS];
    return filterTimelineScreen(screens, ProductionOrderLevelSubScreen.TIMELINE);
  }, []);

  const partLevelSubScreens = useMemo(() => {
    const screens = [PartLevelSubScreen.GENERAL, PartLevelSubScreen.TIMELINE, PartLevelSubScreen.DOCUMENTS];
    return filterTimelineScreen(screens, PartLevelSubScreen.TIMELINE);
  }, []);

  const toolLevelSubScreens = useMemo(() => {
    return [ToolLevelSubScreen.GENERAL];
  }, []);

  return {
    batchLevelSubScreens,
    ncFileLevelSubScreens,
    jobsLevelSubScreens,
    partLevelSubScreens,
    productionOrderLevelSubScreens,
    toolLevelSubScreens,
  };
};
